import { useState, useEffect } from "react";
import xoroLogo from "../assects/img/avironix_logo.png";
import {getMauiDashboardData, mauiUserCreation, mauiUserUrlUpdation} from "../services/apiService"
import AvironixViewLog from "./AvironixViewLog";
import XoroLogout from "../components/XoroLogout";
import thumbsUpBlue from "../assects/img/thumbs-up-blue.png";
import thumbsUpGray from "../assects/img/thumbs-up-grey.png";
import thumbsUpIcon from "../assects/img/thumbs-up-.svg";
import CryptoJS from 'crypto-js';

interface UserData {
  id: number;
  username: string;
  totalLikes: number;
  durationSpent: string;
  lastSummary: string;
  lastSeen: string;
  isLiked: boolean;
  logs?: LogEntry[];
}

interface LogEntry {
  timestamp: string;
  action: string;
  details: string;
}

function AvironixDashboard() {
  const [dashboardData, setDashboardData] = useState<UserData[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [selectedUser, setSelectedUser] = useState<UserData | null>(null);
  const [showLogsModal, setShowLogsModal] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [itemsPerPage] = useState<number>(8); // Show 4 users per page
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [hoveredRowId, setHoveredRowId] = useState<number | null>(null);
  const [avTime, setAvgTime] = useState<string>("");
  const [totalDuration, setTotalDuration] = useState<string>('00:00:00');
  const [selectedUserId,setSelectedUserId] = useState<number>(0);
  
  // Share form states
  const [showShareForm, setShowShareForm] = useState(false);
  const [guestAcc, setGuestAcc] = useState(false);
  const [shareFormData, setShareFormData] = useState({ username: "", mobile: "", interested_in: ""});
  const [createUserUrl, setCreatedUserUrl] = useState("");
  const [adminAccountId, setAdminAccountId] = useState(0); // You'll need to set this based on your auth logic

  const encryptString = (string: any, secretKey: any) => {
      const encryptedString2 = CryptoJS.AES.encrypt(string, secretKey).toString();
      return encodeURIComponent(encryptedString2);
  };

  const decryptString = (encodedCipher: string, secretKey: string) => {
    const cipherB64 = decodeURIComponent(encodedCipher);      
    const bytes = CryptoJS.AES.decrypt(cipherB64, secretKey);
    return bytes.toString(CryptoJS.enc.Utf8);                
  };

const fetchDashboardData = async (): Promise<void> => {
  setLoading(true);
  try {
    const response = await getMauiDashboardData();
    setAvgTime(response.data.average_duration)
    
    if (response.data.users) {
          // Transform the API data to match your UserData interface
          const transformedData = response.data.users.map((user: any, index: number) => {
            // Find the corresponding last_seen entry for this user
            const lastSeenEntry = response.data.last_seen?.find((entry: any) => entry.user_id === user.id);
            
            // Find the corresponding duration entry for this user
          const durationEntry = response.data.user_wise_duration_object?.find((entry: any) => 
      parseInt(entry.user_id) === user.id
    );
        
        return {
          id: user.id || index + 1,
          username: user.username || user.name || `User ${index + 1}`,
          totalLikes: user.total_likes || 0,
          durationSpent: durationEntry?.seconds || '00:00:00',
          lastSummary: user.last_summery || 'No summary available',
          lastSeen: (() => {
            if (lastSeenEntry && lastSeenEntry.lastSeen && lastSeenEntry.lastSeen.end_time) {
              const endTime = new Date(lastSeenEntry.lastSeen.end_time);
              const now = new Date();
              const diffMs = now.getTime() - endTime.getTime();
              const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
              const diffDays = Math.floor(diffHours / 24);
              
              if (diffHours < 1) {
                return 'Less than an hour ago';
              } else if (diffHours < 24) {
                return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
              } else {
                return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
              }
            }
            return 'Unknown';
          })(),
          isLiked: user.is_liked || user.chat_liked || false
        };
      });
      setDashboardData(transformedData);
      setTotalDuration(response.data.total_duration_hms || '00:00:00');
    }
  } catch (error) {
    console.error("Error fetching dashboard data:", error);
  } finally {
    setLoading(false);
  }
};
  const fetchUserLogs = async (userId: number): Promise<LogEntry[]> => {
    try {
      // Using getMauiDashboardData since getMauiUserLogs doesn't exist yet
      console.log(`Fetch logs for user ${userId} - using mock data until API is available`);
      return [
        { timestamp: "2024-08-22 10:30 AM", action: "Started chat session", details: "User initiated conversation" },
        { timestamp: "2024-08-22 10:35 AM", action: "Voice message", details: "Sent audio query about products" },
        { timestamp: "2024-08-22 10:40 AM", action: "Liked response", details: "Liked bot response about AviCargo" },
        { timestamp: "2024-08-22 10:45 AM", action: "Text message", details: "Asked follow-up questions" },
        { timestamp: "2024-08-22 10:50 AM", action: "Session ended", details: "User closed chat interface" }
      ];
    } catch (error) {
      console.error("Error fetching user logs:", error);
      return [];
    }
  };

  const handleViewLogs = async (user: UserData): Promise<void> => {
    setSelectedUser(user);
    const logs = await fetchUserLogs(user.id);
    setSelectedUser({...user, logs});
    setShowLogsModal(true);
  };

  const truncateSummary = (summary: string, maxLength: number = 100): string => {
    return summary.length > maxLength ? `${summary.substring(0, maxLength)}...` : summary;
  };

  // Pagination calculations
  const filteredData = dashboardData.filter(user =>
    user.username.toLowerCase().includes(searchQuery.toLowerCase())
  );
  const totalPages = Math.ceil(filteredData.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const currentUsers = filteredData.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    setCurrentPage(page);
  };

  const goToPrevious = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const goToNext = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1); 
  };

  // Share form handlers (with actual functionality from AvironixTest)
  const handleShareSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    await mauiUserCreation(1, shareFormData.username, shareFormData.mobile, shareFormData.interested_in)
    .then(res => {
        let encodedString = encryptString(`${res.data.user_id}`, 'xoro') 
        let encodedString2 = encryptString(`${adminAccountId}`, 'xoro')
        let url = `https://stagingadmin.xorostory.com/Avironix?user=${encodedString}&account${encodedString2}`
        setCreatedUserUrl(url)
        mauiUserUrlUpdation(res.data.user_id, url)
        fetchDashboardData();
    })
    // Don't close form or reset data here so user can copy/share the URL
    // setShowShareForm(false);
    // setShareFormData({ username: "", mobile: "", interested_in: ""});
  };

  const handleShareFormChange = (field: string, value: string) => {
    setShareFormData(prev => ({ ...prev, [field]: value }));
  };

  useEffect(() => {
    fetchDashboardData();
    // You'll need to set the adminAccountId based on your authentication logic
    // For example, from URL params, localStorage, or API response
    // setAdminAccountId(your_admin_id_here);
  }, []);
 

  return (
    <div className="w-full h-screen flex flex-col bg-gradient-to-br from-slate-50 via-white to-blue-50/30">
      
      {/* Header */}
      <div className="relative bg-white/80 backdrop-blur-xl border-b border-gray-200/60 px-6 py-5">
      


<div className="flex items-center justify-between w-full">
  <div className="flex items-center space-x-4">
    <div className="p-2.5 bg-white rounded-xl shadow-lg border border-gray-200/50">
      <img 
        src={xoroLogo} 
        alt="Avironix Logo" 
        className="w-6 h-6 object-cover rounded-full"
      />
    </div>
    <div>
      <h1 className="text-xl font-bold">
        <span className="text-gray-800">Avironix </span>
        <span className="bg-gradient-to-r from-purple-500 to-pink-500 bg-clip-text text-transparent font-bold">Dashboard</span>
      </h1>
      <p className="text-sm text-gray-500 mt-0.5">Powered By XORO Story</p>
    </div>
  </div>
  
  {/* Right side buttons */}
  <div className="flex items-center space-x-3">
     {/* <button 
        onClick={() => window.location.reload()}
        disabled={loading}
        className="p-2.5 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:from-gray-300 disabled:to-gray-400 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105 disabled:scale-100"
        title="Refresh Page"
      >
      {loading ? (
        <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
      ) : (
        <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
      )}
    </button> */}
    
    {/* Add Share Button */}
    <button 
      onClick={() => setShowShareForm(true)}
      className="p-2.5 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-105"
      title="Create User"
    >
      <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.367 2.684 3 3 0 00-5.367-2.684z" />
      </svg>
    </button>
    
    <XoroLogout />
  </div>
</div>
      </div>

      {/* Dashboard Content */}
      <div className="flex-1 overflow-y-auto p-6">
        <div className="max-w-7xl mx-auto">
          
          {/* Stats Cards */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-200/50 shadow-lg p-6">
              <div className="flex items-center">
                <div className="p-3 bg-blue-100 rounded-xl">
                  <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Users</p>
                  <p className="text-2xl font-bold text-gray-900">{dashboardData.length}</p>
                </div>
              </div>
            </div>
          {/* Updated Total Likes Stats Card */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-200/50 shadow-lg p-6">
             <div className="flex items-center">
                <div className="p-3 bg-purple-100 rounded-xl">
                   <img 
                   src={thumbsUpIcon}
                   alt="thumbs up"
                   className="w-6 h-6"
                   style={{
                   filter: 'brightness(0) saturate(100%) invert(45%) sepia(84%) saturate(2270%) hue-rotate(249deg) brightness(102%) contrast(92%)'
                   }}
                   />
                 </div>
                 <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Total Likes</p>
                  <p className="text-2xl font-bold text-gray-900">
                  {dashboardData.filter(user => user.isLiked).length}
                  </p>
                 </div>
              </div>
           </div>


           
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-200/50 shadow-lg p-6">
              <div className="flex items-center">
                <div className="p-3 bg-green-100 rounded-xl">
                  <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Avg. Duration</p>
                  <p className="text-2xl font-bold text-gray-900">{avTime}</p>
                </div>
              </div>
            </div>
            <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-200/50 shadow-lg p-6">
  <div className="flex items-center">
    <div className="p-3 bg-orange-100 rounded-xl">
      <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    </div>
    <div className="ml-4">
      <p className="text-sm font-medium text-gray-600">Total Duration</p>
      <p className="text-2xl font-bold text-gray-900">{totalDuration}</p>
    </div>
  </div>
</div>

 
          </div>

          {/* Users Table */}
          <div className="bg-white/90 backdrop-blur-sm rounded-2xl border border-gray-200/50 shadow-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200/50">
              <div className="flex items-center justify-between">
                <div className="flex-1 max-w-md">
                  <div className="relative">
                    <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                      <svg className="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                      </svg>
                    </div>
                    <input
                      type="text"
                      placeholder="Search by username..."
                      value={searchQuery}
                      onChange={handleSearchChange}
                      className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-lg text-sm placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                </div>
                <div className="text-sm text-gray-500 ml-4">
                  {searchQuery ? (
                    <>Showing {Math.min(endIndex, filteredData.length)} of {filteredData.length} filtered users</>
                  ) : (
                    <>Showing {startIndex + 1}-{Math.min(endIndex, filteredData.length)} of {filteredData.length} users</>
                  )}
                </div>
              </div>
            </div>
            
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50/50">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Username</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Like Status</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Duration Spent</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Seen</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Last Summary</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">User Logs</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200/50">
                  {currentUsers.map((user) => (
                    <tr key={user.id} className="hover:bg-gray-50/50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
                            <span className="text-white text-sm font-medium">
                              {user.username.split(' ').map(n => n[0]).join('')}
                            </span>
                          </div>
                          <div className="ml-3">
                            <p className="text-sm font-medium text-gray-900">{user.username}</p>
                          </div>
                        </div>
                      </td>
                      {/* Exact design matching your reference image */}
                      <td className="px-6 py-4 whitespace-nowrap">
                         <div className="flex items-center">
                          <img 
                              src={thumbsUpIcon}
                              alt="thumbs up"
                              className={`w-5 h-5 mr-2 ${
                              user.isLiked 
                                ? '' 
                              : 'opacity-40'
                            }`}
                            style={{
                            filter: user.isLiked 
                            ? 'brightness(0) saturate(100%) invert(27%) sepia(51%) saturate(2878%) hue-rotate(206deg) brightness(97%) contrast(97%)' // Blue color
                            : 'brightness(0) saturate(100%) invert(60%) sepia(8%) saturate(564%) hue-rotate(202deg) brightness(92%) contrast(86%)'  // Gray color
                             }}
                          />
                           <span className={`text-sm font-medium ${user.isLiked ? 'text-blue-600' : 'text-gray-500'}`}>
                           {user.isLiked ? 'Liked' : 'Not Liked'}
                           </span>
                         </div>
                      </td>

        
                    
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                          {user.durationSpent}
                        </span>
                      </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                       <div className={`w-2 h-2 rounded-full mr-2 ${
                          (user.lastSeen && typeof user.lastSeen === 'string' && 
                          (user.lastSeen.includes('minutes') || user.lastSeen.includes('hour')))
                          ? 'bg-green-500' 
                          : (user.lastSeen && typeof user.lastSeen === 'string' && user.lastSeen.includes('day'))
                          ? 'bg-yellow-500' 
                         : 'bg-gray-500'
                        }`}></div>
                          <span className="text-sm text-gray-600">{user.lastSeen || 'Unknown'}</span>
                    </div>
                  </td>
                      <td 
                        className="px-6 py-4 relative"
                        onMouseEnter={() => setHoveredRowId(user.id)}
                        onMouseLeave={() => setHoveredRowId(null)}
                      >
                        <p className="text-sm text-gray-900 max-w-xs">
                          {truncateSummary(user.lastSummary, 80)}
                        </p>
                        
                        {/* Tooltip */}
                        {hoveredRowId === user.id && user.lastSummary.length > 80 && (
                          <div className="absolute z-10 left-6 top-full mt-1 p-3 bg-gray-900 text-white text-sm rounded-lg shadow-lg max-w-sm whitespace-normal">
                            {user.lastSummary}
                            <div className="absolute -top-1 left-4 w-2 h-2 bg-gray-900 transform rotate-45"></div>
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => {handleViewLogs(user); setSelectedUserId(user.id)}}
                          className="inline-flex items-center px-3 py-2 border border-transparent text-sm leading-4 font-medium rounded-lg text-white bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 transition-all duration-300 hover:shadow-lg"
                        >
                          <svg className="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          View Logs
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* No Results Message */}
            {filteredData.length === 0 && searchQuery && (
              <div className="px-6 py-8 text-center">
                <svg className="w-12 h-12 text-gray-400 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <p className="text-gray-500 text-sm">No users found matching "{searchQuery}"</p>
                <button
                  onClick={() => setSearchQuery("")}
                  className="mt-2 text-blue-500 hover:text-blue-600 text-sm font-medium"
                >
                  Clear search
                </button>
              </div>
            )}

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="px-6 py-4 border-t border-gray-200/50 flex items-center justify-between">
                <div className="flex items-center text-sm text-gray-500">
                  <span>Page {currentPage} of {totalPages}</span>
                </div>
                
                <div className="flex items-center space-x-2">
                  <button
                    onClick={goToPrevious}
                    disabled={currentPage === 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>
                  
                  <div className="flex items-center space-x-1">
                    {Array.from({ length: totalPages }, (_, i) => i + 1)
                      .filter(page => {
                        // Show first page, last page, current page, and 2 pages around current
                        return page === 1 || 
                               page === totalPages || 
                               (page >= currentPage - 1 && page <= currentPage + 1);
                      })
                      .map((page, index, array) => {
                        // Add ellipsis if there's a gap
                        const prevPage = array[index - 1];
                        const showEllipsis = prevPage && page - prevPage > 1;
                        
                        return (
                          <div key={page} className="flex items-center">
                            {showEllipsis && (
                              <span className="px-2 py-1 text-gray-400">...</span>
                            )}
                            <button
                              onClick={() => goToPage(page)}
                              className={`px-3 py-2 text-sm font-medium rounded-lg transition-colors ${
                                currentPage === page
                                  ? 'bg-blue-500 text-white'
                                  : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50'
                              }`}
                            >
                              {page}
                            </button>
                          </div>
                        );
                      })}
                  </div>
                  
                  <button
                    onClick={goToNext}
                    disabled={currentPage === totalPages}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

{/* Share Form Modal */}
{showShareForm && (
  <div className="fixed inset-0 bg-black/20 backdrop-blur-sm flex items-center justify-center z-50 px-4">
    <div className="bg-white/95 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-2xl p-6 max-w-sm w-full">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center">
          <h3 className="text-lg font-bold text-gray-800 me-3">{guestAcc ? "Guest" : "Create User"}</h3>
          {
            guestAcc ?
              <button
                type="button"
                onClick={()=>setGuestAcc(false)}
                className="flex-1 px-3 py-2 bg-gradient-to-r from-blue-400 to-purple-500 hover:from-blue-500 hover:to-purple-600 text-white rounded-xl transition-all hover:shadow-lg text-sm font-medium"
              >
                Create User
              </button>
            :
              <button
                type="button"
                onClick={()=>setGuestAcc(true)}
                className="flex-1 px-3 py-2 bg-gradient-to-r from-blue-400 to-purple-500 hover:from-blue-500 hover:to-purple-600 text-white rounded-xl transition-all hover:shadow-lg text-sm font-medium"
              >
                Guest 
              </button>
          }
        </div>
        <button 
          onClick={() => {
            setShareFormData({ username: "", mobile: "", interested_in: ""});
            setCreatedUserUrl("")
            setShowShareForm(false)
          }}
          className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>
      
      {!guestAcc ?
      <form onSubmit={handleShareSubmit} className="space-y-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
          <input
            type="text"
            value={shareFormData.username}
            onChange={(e) => handleShareFormChange("username", e.target.value)}
            placeholder="Enter username"
            className="w-full px-4 py-3 bg-white/90 border border-gray-200/50 rounded-xl focus:outline-none focus:border-blue-300/50 text-gray-800 placeholder-gray-500 text-sm"
            required
          />
        </div>
        
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Mobile Number</label>
          <input
            type="tel"
            value={shareFormData.mobile}
            onChange={(e) => handleShareFormChange("mobile", e.target.value)}
            placeholder="Enter mobile number"
            className="w-full px-4 py-3 bg-white/90 border border-gray-200/50 rounded-xl focus:outline-none focus:border-blue-300/50 text-gray-800 placeholder-gray-500 text-sm"
            required
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">More Info</label>
          <input
            type="text"
            value={shareFormData.interested_in}
            onChange={(e) => handleShareFormChange("interested_in", e.target.value)}
            placeholder="Enter more details(Interested part)"
            className="w-full px-4 py-3 bg-white/90 border border-gray-200/50 rounded-xl focus:outline-none focus:border-blue-300/50 text-gray-800 placeholder-gray-500 text-sm"
            required
          />
        </div>
        
        <div className="flex space-x-3 pt-2">
          <button
            type="button"
            onClick={() =>{
              setShareFormData({ username: "", mobile: "", interested_in: ""});
              setCreatedUserUrl("")
              setShowShareForm(false)
            }}
            className="flex-1 px-4 py-3 bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-xl transition-colors text-sm font-medium"
          >
            Cancel
          </button>
          <button
            type="submit"
            className="flex-1 px-4 py-3 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 text-white rounded-xl transition-all hover:shadow-lg text-sm font-medium"
          >
            Create
          </button>
        </div>

        {createUserUrl && (
          <div className="pt-3 space-y-2">
            <p className="font-medium">Copy or share URL:</p>
            <button
              type="button"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(createUserUrl);
                  alert("Link copied to clipboard. Paste it into any app to share.");
                } catch {
                  const wa = `https://wa.me/?text=${encodeURIComponent(createUserUrl)}`;
                  window.open(wa, "_blank");
                }
              }}
              className="w-full px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-all hover:shadow-lg text-sm font-medium"
            >
              Copy URL
            </button>

            <button
              type="button"
              onClick={async () => {
                const title = `Avironix Assistant — Invite: \nHey ${shareFormData.username}! Here is your link:`;
                const url   = createUserUrl;

                if (navigator.share) {
                  try {
                    await navigator.share({ title, url });
                    return;
                  } catch (err) {
                  }
                }
              }}
              className="w-full px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-all hover:shadow-lg text-sm font-medium"
            >
              Share
            </button>
          </div>
        )}
      </form>
      :
      <div>
        <button
          type="button"
          onClick={async () => {
            try {
              await navigator.clipboard.writeText("https://stagingadmin.xorostory.com/Avironix?user=U2FsdGVkX1%2BdyBpOK9vo2FjPu72lXC2blpPv4txYguk%3D");
              alert("Link copied to clipboard. Paste it into any app to share.");
            } catch {
              const wa = `https://wa.me/?text=${encodeURIComponent("https://stagingadmin.xorostory.com/Avironix?user=U2FsdGVkX1%2BdyBpOK9vo2FjPu72lXC2blpPv4txYguk%3D")}`;
              window.open(wa, "_blank");
            }
          }}
          className="w-full px-4 py-3 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-all hover:shadow-lg text-sm font-medium"
        >
          Copy URL
        </button>
        <button
          type="button"
          onClick={async () => {
            const title = `Avironix Assistant — Invite: \nHey! Here is your link:`;
            const url   = "https://stagingadmin.xorostory.com/Avironix?user=U2FsdGVkX1%2BdyBpOK9vo2FjPu72lXC2blpPv4txYguk%3D";

            if (navigator.share) {
              try {
                await navigator.share({ title, url });
                return;
              } catch (err) {
              }
            }
          }}
          className="w-full px-4 py-3 mt-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-xl transition-all hover:shadow-lg text-sm font-medium"
        >
          Share
        </button>
      </div>
      }
    </div>
  </div>
)}

{/* Improved Logs Modal */}
{showLogsModal && selectedUser && (
  <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
    <div className="w-full max-w-4xl mx-auto bg-white/95 backdrop-blur-xl rounded-2xl border border-gray-200/50 shadow-2xl max-h-[85vh] overflow-hidden flex flex-col">
      
      {/* Modal Header */}
      <div className="flex-shrink-0 relative bg-white/80 backdrop-blur-xl border-b border-gray-200/60 px-6 py-5">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center">
              <span className="text-white text-sm font-medium">
                {selectedUser.username.split(' ').map(n => n[0]).join('')}
              </span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                {selectedUser.username}'s Chat History
              </h2>
              <p className="text-sm text-gray-500">
                Duration: {selectedUser.durationSpent} • Last seen: {selectedUser.lastSeen}
              </p>
            </div>
          </div>
          
          {/* Close Button */}
          <button
            onClick={() => {
              setShowLogsModal(false);
              setSelectedUser(null);
              setSelectedUserId(0);
            }}
            className="p-2 bg-gray-100 hover:bg-gray-200 text-gray-600 hover:text-gray-800 rounded-xl transition-all duration-200 shadow-sm hover:shadow-md"
            title="Close"
          >
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      </div>

      {/* Modal Content */}
      <div className="flex-1 bg-gradient-to-br from-slate-50/50 via-white to-blue-50/30 overflow-hidden p-4">
        <div className="h-full">
          <AvironixViewLog user_id={selectedUserId}/>
        </div>
      </div>
    </div>
  </div>
)}
  
    </div>
  );
}

export default AvironixDashboard;